import {
  ModelForm,
  ModelSelect,
  FlexColumn,
  ModelDateInline,
  EmptyData,
  Tooltip,
  useDeleteAlert,
  Errors,
  useWarningAlert,
  Flex,
  useCreateAndAddRefs,
  ValidIcon,
  InvalidIcon,
  ModelNewNumericInput,
  OutsideClicker,
  SelectInputDropdown,
  DropdownItem,
  TripleToggleInput,
  SectionIconButton,
  TextFieldInput,
  IconButton,
  flattenErrorTypeArray,
  ErrorTypes,
  WarningIcon,
  useStyledTheme,
} from '@equifinance/shared';
import { PropsWithChildren, useMemo, useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { DateFormatStringEnum } from '@equifinance/shared/utils';
import merge from 'lodash/merge';
import { useTabNavHeight } from '../AccountTabNav';
import {
  CreditItem,
  CreditTypes,
  ConsolidationType,
  addressTypeFromAddress,
  UnsecuredAccountType,
  SecuredAccountType,
  AddressTypes,
  RevolvingAccountTypes,
  CreditAddress,
  useCreditContext,
  IgnoredByType,
  IgnoredReasonType,
  IgnoredReasonDescriptions,
  IgnoredReasonTags,
  checkCreditItemIsSettled,
  Insolvency,
  checkCreditItemIsDefaulted,
} from '../../contexts/credit';
import SecuredCreditDetails from './CreditDetails/SecuredCreditDetails/SecuredCreditDetails';
import UnsecuredCreditDetails from './CreditDetails/UnsecuredCreditDetails/UnsecuredCreditDetails';
import CreditTableAddress from './CreditComponents/CreditTableAddress';
import CreditTable, { creditLineErrorTypes, creditLineWarningTypes } from './CreditTable';
import { ApplicantName } from './CreditTables';
import useIgnoreByType from './hooks/useIgnoreByType';
import useIgnoreDropdownOptions from './hooks/useIgnoreDropdownOptions';

const Icon = styled.i` ${({ theme }) => `
  color:${theme.colours.primary[1]};
  font-size:${theme.fontSize[4]};
`}`;

const DeleteIcon = styled(Icon)` ${({ theme }) => `
  color:${theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`}`;

const IgnoreIcon = styled(Icon) <{ disabled?: boolean }>` ${({ theme, disabled }) => `
  color: ${disabled ? theme.colours.neutral[6] : theme.colours.primary[1]};
  font-size: ${theme.fontSize[3]};
`}`;

const StatusIconContainer = styled.div` ${({ theme }) => `
  width: ${theme.padding[6]};
  display: flex;
`}`;

const CreditOptionsContainer = styled(Flex)` ${({ theme }) => `
  gap:${theme.padding[4]};
`}`;

const TableContainer = styled(FlexColumn)` ${({ theme }) => `
  padding-top: ${theme.padding[4]};
  gap: ${theme.padding[1]};
  
  ${SectionIconButton} {
    :not([disabled]) {
      color: ${theme.colours.primary[1]};
      :hover {
        transform: none;
      }
    }
  }
  
  ${TextFieldInput} {
    &:disabled::placeholder {
      color:  ${theme.colours.neutral[2]};
    }
  }
`}`;

const InfoItemContainer = styled.div` ${({ theme }) => `
  padding: ${theme.padding[2]};
  padding-top: ${theme.padding[1]};
  padding-bottom: ${theme.padding[1]};
  border-radius: ${theme.borderRadius[1]};
`}`;

const IgnoredItemContainer = styled(InfoItemContainer) <{ bg?: string }>` ${({ theme, bg }) => `
  color: ${theme.colours.neutral[10]};
  background: ${bg || theme.colours.neutral[4]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const IgnoredReasonDropdown = styled(SelectInputDropdown)`${({ theme }) => `
  min-width: ${theme.ignoredDropdownWidth};
  top: ${theme.fontSize[4]};
  right: -${theme.padding[2]};
`}`;

export function IgnoredItem({ id = '', children = 'Ignored', bg = '' }: PropsWithChildren<{ id?: string, bg?: string }>) {
  return (
    <IgnoredItemContainer id={id} bg={bg}>
      {children}
    </IgnoredItemContainer>
  );
}

export function IgnoredTag({ data }: { data: CreditItem | Insolvency }) {
  const { id, ignoredReason, ignoredBy } = data;
  const theme = useStyledTheme();
  const bg = useMemo(() => {
    const { ignored, ignoredBy } = data;
    return ignored && (ignoredBy === IgnoredByType.System || ignoredBy === IgnoredByType.Underwriter) ? theme.colours.neutral[4] : theme.colours.primary[3];
  }, [data]);
  const ignoredByName = ignoredBy === 'SalesSupport' ? 'Sales Support' : ignoredBy;

  const tooltipContent = `${IgnoredReasonDescriptions[ignoredReason]}<br/><center>(${ignoredByName})</center>`;

  return (
    <IgnoredItem id={`ignored-reason-${id}`} bg={bg}>
      {IgnoredReasonTags[ignoredReason]}
      <Tooltip
        id={`ignored-reason-${id}`}
        html={tooltipContent}
        place="bottom"
      />
    </IgnoredItem>
  );
}

const SettledItemContainer = styled(InfoItemContainer)` ${({ theme }) => `
  color: ${theme.colours.neutral[10]};
  background: ${theme.colours.neutral[4]};
  font-weight: ${theme.fontWeight.bold};
`}`;

export function SettledItem() {
  return (
    <SettledItemContainer>Settled</SettledItemContainer>
  );
}

export function DefaultedItem() {
  return (
    <SettledItemContainer>Defaulted</SettledItemContainer>
  );
}

function StatusIcon(creditItem: CreditItem, type: 'secured' | 'unsecured') {
  const theme = useStyledTheme();
  const { id, ignored, endDate } = creditItem;
  const { credit: { errors, warnings } } = useCreditContext();
  const itemErrors = flattenErrorTypeArray(errors?.[id] || []);
  const hasValidationFailures = itemErrors.some((error) => error.errorType === ErrorTypes.ValidationFailure);
  const completed = !hasValidationFailures;

  const isHidden = useMemo(() => {
    const isSettled = checkCreditItemIsSettled(creditItem);
    if (type === 'secured') {
      const isSettledOverAYear = isSettled && moment().diff(moment(endDate), 'M') > 12;
      return ignored || isSettledOverAYear;
    }
    return ignored || isSettled;
  }, [creditItem, type]);

  if (isHidden) {
    return null;
  }

  const itemErrorsAndWarnings = flattenErrorTypeArray(
    merge({}, errors, warnings)[id]
      ?.filter((e) => e.propertyName == null),
  );
  const isItemWarning = itemErrorsAndWarnings.find((e) => creditLineWarningTypes.includes(e.errorType));
  const isItemError = itemErrorsAndWarnings.find((e) => creditLineErrorTypes.includes(e.errorType));

  if (isItemWarning || isItemError) {
    return (
      <StatusIconContainer>
        <WarningIcon
          stroke={isItemWarning ? theme.colours.secondary[1] : theme.colours.danger[1]}
          fill={theme.colours.neutral[10]}
        />
      </StatusIconContainer>
    );
  }

  return completed ? <ValidIcon /> : <InvalidIcon />;
}

export function IgnoreButton({ data, update, accountType }: {
  data: CreditItem | Insolvency,
  update: (values: any, disableIncrementUpdateCount?: boolean) => Promise<void>,
  accountType: string,
}) {
  const [isOpen, setIsOpen] = useState(false);

  const {
    id,
    isEquifax,
    ignored,
    canUnignore,
    ignoreOptions,
  } = data;

  const ignoreBy = useIgnoreByType();

  const ignoreDropdownOptions = useIgnoreDropdownOptions(ignoreOptions || []);

  const ignoreDisabled = !ignored && !accountType;

  const warningAlert = useWarningAlert();

  const onClickIgnore = (reason?: IgnoredReasonType) => {
    if (ignoreDisabled) return;
    if (!ignored && !reason) {
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
    const ignoreText = ignored ? 'unignore' : 'ignore';
    const ignoreTextCapitalised = ignoreText.charAt(0).toUpperCase() + ignoreText.slice(1);
    const params = {
      ignored: !ignored,
      ignoredBy: ignored ? null : ignoreBy,
      ignoredReason: ignored ? null : reason,
    };

    warningAlert({
      onConfirm: () => update(params),
      content: `Are you sure you want to ${ignoreText} this credit line?`,
      title: ignoreTextCapitalised,
      confirmLabel: ignoreTextCapitalised,
    });
  };

  const canShowIgnoreButton = (!ignored && !!ignoreDropdownOptions.length) || (ignored && ignoreBy && canUnignore?.includes(ignoreBy));

  const isIgnoreVisible = canShowIgnoreButton && isEquifax;

  return isIgnoreVisible ? (
    <>
      <IconButton disabled={ignoreDisabled} onClick={() => onClickIgnore()}>
        <IgnoreIcon
          id={`ignore-credit-${id}`}
          disabled={ignoreDisabled}
          className={ignored ? 'mi-circle-add' : 'mi-circle-remove'}
        />
      </IconButton>
      {!ignoreDisabled && !isOpen && (
        <Tooltip
          id={`ignore-credit-${id}`}
          html={ignored ? 'Unignore credit line' : 'Ignore credit line'}
          place="bottom"
        />
      )}
      {isOpen && !ignoreDisabled && (
        <OutsideClicker
          onClickOutside={() => setIsOpen(false)}
        >
          <IgnoredReasonDropdown id={`${id}-ingore-reason-select`}>
            {ignoreDropdownOptions.map((reason) => (
              <DropdownItem
                key={`${id}-ingore-reason-option-${reason}`}
                id={`${id}-ingore-reason-option-${reason}`}
                onClick={() => onClickIgnore(reason)}
                focused={false}
              >
                {IgnoredReasonDescriptions[reason]}
              </DropdownItem>
            ))}
          </IgnoredReasonDropdown>
        </OutsideClicker>
      )}
    </>
  ) : <></>;
}

export function DeleteButton({ onDelete, id }: {
  onDelete: () => void,
  id: string,
}) {
  return (
    <>
      <IconButton onClick={onDelete}>
        <DeleteIcon
          id={`delete-credit-${id}`}
          className="mi-delete"
        />
      </IconButton>
      <Tooltip
        id={`delete-credit-${id}`}
        html="Delete credit line"
        place="bottom"
      />
    </>
  );
}

function CreditOptions(
  creditItem: CreditItem,
  type: 'secured' | 'unsecured',
  deleteItems: (creditItem: CreditItem, type: CreditTypes) => void,
  update: (values: any, disableIncrementUpdateCount?: boolean) => Promise<void>,
) {
  const {
    id,
    isEquifax,
    accountType,
  } = creditItem;

  const deleteAlert = useDeleteAlert();

  const onDelete = () => {
    deleteAlert({
      onConfirm: () => deleteItems(creditItem, type === 'secured' ? CreditTypes.SECURED : CreditTypes.UNSECURED),
      text: 'Are you sure you want to delete this credit line?',
    });
  };

  return (
    <CreditOptionsContainer>
      <IgnoreButton data={creditItem} update={update} accountType={accountType} />
      {!isEquifax && <DeleteButton onDelete={onDelete} id={id} />}
    </CreditOptionsContainer>
  );
}

function CreditStartDate(
  creditItem: CreditItem,
  baseUpdate: (creditItem: CreditItem, values: any, disableIncrementUpdateCount?: boolean) => void,
  errors: Errors,
  warnings: Errors,
) {
  const {
    id, isEquifax, ignored,
  } = creditItem;

  const [showOverrideError, setShowOverrideError] = useState<boolean>(false);

  const updateFunc = (values: any, disableIncrementUpdateCount?: boolean) => {
    if (!values.startDate) {
      setShowOverrideError(false);
      baseUpdate(creditItem, { startDate: null }, disableIncrementUpdateCount);
      return;
    }

    const date = moment(values.startDate, DateFormatStringEnum.ISO8601, true);
    const isValid = date.isValid() && date.year() > 1900;
    setShowOverrideError(!isValid);
    if (isValid) {
      baseUpdate(creditItem, values, disableIncrementUpdateCount);
    }
  };

  return (
    <ModelForm model={creditItem} update={updateFunc} errors={errors} warnings={warnings}>
      <ModelDateInline
        id={id}
        field="startDate"
        placeholder={isEquifax ? '-' : 'DD/MM/YYYY'}
        disabled={isEquifax || ignored}
        errorOverride={showOverrideError ? 'Date is invalid' : ''}
      />
    </ModelForm>
  );
}

export default function CreditTableItem({
  address,
  type,
}: {
  address: CreditAddress,
  type: 'secured' | 'unsecured',
}) {
  const {
    credit,
    securedCredit,
    unsecuredCredit,
    addCreditItem,
    updateCreditItem,
    deleteCreditItems,
  } = useCreditContext();

  const {
    errors,
    warnings,
  } = credit;

  const creditItems = Object.values({
    securedCredit,
    unsecuredCredit,
  }[`${type}Credit`]).filter((ci) => ci?.id);

  const baseUpdate = (creditItem: CreditItem, values: any, disableIncrementUpdateCount: boolean = false) => updateCreditItem(
    values,
    creditItem.id,
    type === 'secured' ? CreditTypes.SECURED : CreditTypes.UNSECURED,
    disableIncrementUpdateCount,
  );

  const update = (creditItem: CreditItem) => (values: any, disableIncrementUpdateCount: boolean = false) => baseUpdate(
    creditItem,
    values,
    disableIncrementUpdateCount,
  );

  const CreditType = (creditItem: CreditItem) => useMemo(() => {
    const isSettledOverAYear = checkCreditItemIsSettled(creditItem)
      && moment().diff(moment(creditItem.endDate), 'M') > 12;
    return (
      <ModelForm model={creditItem} update={update(creditItem)} errors={errors} warnings={warnings}>
        <ModelSelect
          id={creditItem.id}
          field="accountType"
          placeholder="Please select"
          options={type === 'secured' ? SecuredAccountType : UnsecuredAccountType}
          showValueTooltip="onHover"
          disabled={
            type === 'secured' ? creditItem.ignored || isSettledOverAYear : creditItem.isEquifax
          }
          isTooltipError
        />
      </ModelForm>
    );
  }, [creditItems]);

  const CreditConsolidation = (creditItem: CreditItem, addressType: AddressTypes) => useMemo(
    () => {
      if (creditItem.ignored) {
        return creditItem.isEquifax ? <IgnoredTag data={creditItem} /> : null;
      }
      if (checkCreditItemIsSettled(creditItem)) {
        return creditItem.isEquifax ? <SettledItem /> : null;
      }
      if (checkCreditItemIsDefaulted(creditItem)) {
        return creditItem.isEquifax ? <DefaultedItem /> : null;
      }
      return (
        <ModelForm model={creditItem} update={update(creditItem)} errors={errors} warnings={warnings}>
          <ModelSelect
            id={creditItem.id}
            field="consolidationType"
            options={{
              ...(type === 'secured' && (addressType === 'current' || addressType === 'other') ? {
                'NoConsolidation': ConsolidationType.NoConsolidation,
                'Partial': ConsolidationType.Partial,
                'Full': ConsolidationType.Full,
              } : {}),
              ...(type === 'secured' && (addressType === 'security') ? {
                'NoConsolidation': ConsolidationType.NoConsolidation,
                ...(creditItem.accountType === 'OtherProperty' ? ({
                  'Partial': ConsolidationType.Partial,
                }) : ({
                  'Arrears': ConsolidationType.Arrears,
                })),
                'Full': ConsolidationType.Full,
              } : {}),
              ...(type === 'unsecured' ? {
                'NoConsolidation': ConsolidationType.NoConsolidation,
                'Partial': ConsolidationType.Partial,
                'Full': ConsolidationType.Full,
              } : {}),
            }}
            disabled={!creditItem.accountType || (type === 'unsecured' && creditItem.isUnderIVA)}
            placeholder="Please select"
            isTooltipError
          />
        </ModelForm>
      );
    },
    [creditItems],
  );

  const CreditBalance = (creditItem: CreditItem) => useMemo(() => {
    const field = creditItem.isEquifax ? 'balance' : 'actualBalance';

    return (
      <ModelForm model={creditItem} update={update(creditItem)} errors={errors} warnings={warnings}>
        <ModelNewNumericInput
          id={`${creditItem.id}-${field}`}
          field={field}
          placeholder={creditItem.isEquifax ? '-' : ''}
          disabled={
            creditItem.isEquifax
            || creditItem?.ignored
            || (type === 'unsecured' && creditItem.isUnderIVA)
          }
          isFixedDecimal
          isTooltipError
        />
      </ModelForm>
    );
  }, [creditItems]);

  const CRLimStartBalance = (creditItem: CreditItem) => useMemo(
    () => {
      const field = RevolvingAccountTypes.includes(creditItem?.accountType) ? (
        creditItem.isEquifax ? 'creditLimit' : 'manualCreditLimit'
      ) : (
        creditItem.isEquifax ? 'openingBalance' : 'manualOpeningBalance'
      );

      return (
        <ModelForm model={creditItem} update={update(creditItem)} errors={errors} warnings={warnings}>
          <ModelNewNumericInput
            id={`${creditItem.id}-${field}`}
            field={field}
            placeholder={creditItem.isEquifax ? '-' : ''}
            disabled={creditItem.isEquifax || (type === 'unsecured' && creditItem.isUnderIVA)}
            isTooltipError
            isFixedDecimal
          />
        </ModelForm>
      );
    },
    [creditItems],
  );

  const CreditConsolidatedAmount = (creditItem: CreditItem) => useMemo(
    () => {
      if (creditItem.ignored || checkCreditItemIsSettled(creditItem)) {
        return null;
      }

      const disabled = !creditItem.accountType
        || !creditItem.consolidationType
        || creditItem.consolidationType === 'NoConsolidation';

      return (
        <ModelForm model={creditItem} update={update(creditItem)} errors={errors} warnings={warnings}>
          <ModelNewNumericInput
            id={`${creditItem.id}-consolidatedAmount`}
            field="consolidatedAmount"
            disabled={disabled}
            isFixedDecimal
            signed
            isTooltipError
            errorOverride={creditItem.consolidatedAmount < 0 ? 'This field must be greater than 0.' : undefined}
            touchOverride={creditItem.consolidatedAmount < 0}
          />
        </ModelForm>
      );
    },
    [creditItems],
  );

  const IVA = (creditItem: CreditItem) => useMemo(
    () => {
      const { ignored, ignoredReason, accountType } = creditItem;
      const isUnsecured = type === 'unsecured';
      const disabled = creditItem.isEquifax
        ? checkCreditItemIsSettled(creditItem) || checkCreditItemIsDefaulted(creditItem) || (ignored && (!isUnsecured || ignoredReason !== IgnoredReasonType.ExpiringCredit))
        : !accountType;

      return (
        <TripleToggleInput
          id={`${creditItem}-isUnderIVA`}
          disabled={disabled}
          value={creditItem.isUnderIVA}
          onChange={(value: boolean) => update(creditItem)({ isUnderIVA: value })}
        />
      );
    },
    [creditItems],
  );

  const creditHistorySelector = (creditItem: CreditItem) => {
    if (!creditItem?.isEquifax) return 'Manual input';
    if (!creditItem.paymentHistory?.length) {
      return '-';
    }
    return creditItem?.paymentHistory?.map(({ status }) => status).slice(0, 12).join('') || '-';
  };

  const columns = (addressType: AddressTypes) => ([
    ...(process.env.REACT_APP_DEBUG ? [{
      name: 'id',
      sortable: false,
      selector: (creditItem: CreditItem) => creditItem?.id,
      width: '60px',
    }] : []),
    {
      name: 'Status',
      sortable: false,
      cell: (creditItem: CreditItem) => StatusIcon(creditItem, type),
      width: '60px',
      style: {
        justifyContent: 'center !important',
      },
    },
    {
      name: 'Applicant',
      sortable: false,
      minWidth: '200px',
      cell: (creditItem: CreditItem) => ApplicantName(
        creditItem?.id,
        creditItem?.isEquifax,
        creditItem?.ignored,
        creditItem?.nameMatchedData,
        credit?.applicants,
        creditItem,
        update(creditItem),
      ),
    },
    {
      name: 'Type',
      sortable: false,
      minWidth: '150px',
      maxWidth: '260px',
      cell: (creditItem: CreditItem) => CreditType(creditItem),
    },
    {
      name: 'Credit history',
      sortable: false,
      width: '120px',
      selector: creditHistorySelector,
    },
    {
      name: 'Start date',
      sortable: false,
      width: '140px',
      cell: (creditItem: CreditItem) => CreditStartDate(creditItem, baseUpdate, errors, warnings),
    },
    {
      name: 'Balance (£)',
      sortable: false,
      minWidth: '120px',
      maxWidth: '200px',
      cell: (creditItem: CreditItem) => CreditBalance(creditItem),
    },
    ...(type === 'unsecured' ? [{
      name: (
        <>
          CR lim./
          <br />
          Start bal. (£)
        </>
      ),
      sortable: false,
      minWidth: '120px',
      maxWidth: '200px',
      cell: (creditItem: CreditItem) => CRLimStartBalance(creditItem),
    }] : []),
    {
      name: 'Consol',
      sortable: false,
      minWidth: '120px',
      maxWidth: '200px',
      cell: (creditItem: CreditItem) => CreditConsolidation(creditItem, addressType),
    },
    {
      name: 'Consol amount (£)',
      sortable: false,
      minWidth: '120px',
      maxWidth: '200px',
      cell: (creditItem: CreditItem) => CreditConsolidatedAmount(creditItem),
    },
    ...(type === 'unsecured' ? [{
      name: 'IVA',
      sortable: false,
      width: '52px',
      cell: (creditItem: CreditItem) => IVA(creditItem),
    }] : []),
    {
      cell: (creditItem: CreditItem) => CreditOptions(
        creditItem,
        type,
        deleteCreditItems,
        update(creditItem),
      ),
      sortable: false,
      width: '35px',
    },
  ]);

  const expandableRowsComponent = type === 'secured' ? SecuredCreditDetails : UnsecuredCreditDetails;

  const stickyHeaderOffset = useTabNavHeight() + 36;

  const addressType = addressTypeFromAddress(address);

  const addressId = address?.id;

  const data = creditItems
    .filter((creditItem: CreditItem) => creditItem.addressId === addressId)
    .sort((a, b) => a.ordinal - b.ordinal);
  const hasItems = !!data.length;

  const ref = useCreateAndAddRefs(`${type}-credit-address-${addressId}`);

  const initiatorId = `${type}-credit-address-${addressId}-add-button`;

  const onClickAddCreditItem = async (addCreditAddressId: string) => {
    await addCreditItem({
      addressId: addCreditAddressId,
    }, type === 'secured' ? CreditTypes.SECURED : CreditTypes.UNSECURED, initiatorId);
  };

  return (
    <TableContainer key={addressId}>
      <div id={`${type}-credit-address-${addressId}`} ref={ref}>

        <CreditTableAddress
          address={address}
          onClickAddCreditItem={() => onClickAddCreditItem(addressId)}
          hasItems={hasItems}
          id={initiatorId}
        />

        {hasItems && (
          <CreditTable
            id={`${type}-credit-table`}
            columns={columns(addressType)}
            data={data}
            expandableRowsComponent={expandableRowsComponent}
            expandableRowDisabled={(creditItem: CreditItem) => !creditItem.isEquifax && (!creditItem.accountType || !creditItem.consolidationType)}
            expandableRowsComponentProps={{
              addressType,
              errors,
              warnings,
            }}
            showDisabledExpandableIcon
            showSearch={false}
            stickyHeaderOffset={stickyHeaderOffset}
            noDataComponent={(
              <EmptyData
                title="There are no results from the credit bureau."
                message="Please add security details manually."
              />
            )}
          />
        )}
      </div>
    </TableContainer>
  );
}
